import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { useTaskRuntime } from "../../../context/TaskRuntimeContext";
import { buildGRBASFeedback } from "../services/buildGRBASFeedback";

export default function useGRBASTask() {
  const {
    session,
    task,
    step,
    responses = [],
    apiUrl,
    token,
    advanceStandaloneSession,
    onStandaloneComplete,
    referenceFeedbackEnabled = true,
    onRuntimeSubmitted,
  } = useTaskRuntime();
  const [samples, setSamples] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackRows, setFeedbackRows] = useState([]);
  const [currentResponse, setCurrentResponse] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const trials = useMemo(
    () => session?.presentedTrials || [],
    [session]
  );

  useEffect(() => {
    setShowFeedback(false);
    setFeedbackRows([]);
    setCurrentResponse(null);
    setSubmitting(false);
    setIsCompleted(false);
  }, [session?._id]);

  useEffect(() => {
    if (!trials.length) {
      return;
    }

    const answeredTrialIds = new Set(
      responses.map((response) => String(response.trialId))
    );

    const nextUnansweredIndex = trials.findIndex(
      (trial) => !answeredTrialIds.has(String(trial.trialId))
    );

    if (nextUnansweredIndex === -1) {
      setCurrentIndex(Math.max(trials.length - 1, 0));
    } else {
      setCurrentIndex(nextUnansweredIndex);
    }
  }, [responses, trials]);

  useEffect(() => {
    let isMounted = true;

    async function fetchSamples() {
      const ids = [
        ...new Set(
          trials
            .map((trial) => trial?.stimuli?.[0]?.voiceSampleId)
            .filter(Boolean)
            .map(String)
        )
      ];

      const entries = await Promise.all(
        ids.map(async (sampleId) => {
          const response = await axios.get(
            `${apiUrl}/voice-samples/${sampleId}`
          );

          return [sampleId, response.data.data];
        })
      );
      const nextSamples = Object.fromEntries(entries);

      if (isMounted) {
        setSamples(nextSamples);
      }
    }

    if (trials.length) {
      fetchSamples().catch((error) => {
        console.error("❌ GRBAS sample load error:", error);
      });
    }

    return () => {
      isMounted = false;
    };
  }, [apiUrl, trials]);

  const currentTrial = trials[currentIndex] || null;
  const currentSampleId =
    currentTrial?.stimuli?.[0]?.voiceSampleId
      ? String(currentTrial.stimuli[0].voiceSampleId)
      : null;
  const currentSample =
    currentSampleId ? samples[currentSampleId] || null : null;
  const isContinuousPractice =
    task?.uiConfig?.practiceFlow === "continuous";

  async function submitEvaluation(formData) {
    if (!currentTrial) {
      return;
    }

    const responsePayload = {
      overallSeverity: {
        ordinalValue: Number(formData.overallSeverity)
      },
      roughness: {
        ordinalValue: Number(formData.roughness)
      },
      breathiness: {
        ordinalValue: Number(formData.breathiness)
      },
      asthenia: {
        ordinalValue: Number(formData.asthenia)
      },
      strain: {
        ordinalValue: Number(formData.strain)
      }
    };

    setSubmitting(true);

    try {
      await axios.post(
        `${apiUrl}/evaluation/response`,
        {
          sessionId: session._id,
          trialId: currentTrial.trialId,
          trialKey: currentTrial.trialKey,
          stimulusInstanceId:
            currentTrial.stimuli[0].stimulusInstanceId,
          responseType: "grbas",
          response: responsePayload
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (referenceFeedbackEnabled) {
        const referenceResponse = await axios.get(
          `${apiUrl}/evaluation/session/${session._id}/trial/${currentTrial.trialId}/reference`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        setCurrentResponse(responsePayload);
        setFeedbackRows(
          buildGRBASFeedback({
            response: responsePayload,
            reference: referenceResponse.data.reference
          })
        );
        setShowFeedback(true);
        return;
      }

      setCurrentResponse(responsePayload);

      if (onRuntimeSubmitted) {
        await onRuntimeSubmitted(responsePayload);
        return;
      }

      setIsCompleted(true);
    } catch (error) {
      console.error("❌ GRBAS submit error:", error);
    } finally {
      setSubmitting(false);
    }
  }

  async function next() {
    const isLastSample =
      currentIndex + 1 >= trials.length;

    if (!isLastSample) {
      setShowFeedback(false);
      setFeedbackRows([]);
      setCurrentResponse(null);
      setCurrentIndex((value) => value + 1);
      return;
    }

    setSubmitting(true);

    try {
      if (onStandaloneComplete) {
        setShowFeedback(false);
        setFeedbackRows([]);
        setCurrentResponse(null);
        await onStandaloneComplete();
        return;
      }

      if (isContinuousPractice && advanceStandaloneSession) {
        setShowFeedback(false);
        setFeedbackRows([]);
        setCurrentResponse(null);
        await advanceStandaloneSession();
        return;
      }

      setIsCompleted(true);
      setShowFeedback(false);
      setFeedbackRows([]);
      setCurrentResponse(null);
    } catch (error) {
      console.error("❌ Standalone session advance error:", error);
    } finally {
      setSubmitting(false);
    }
  }

  const totalSamples = trials.length;
  const progress =
    totalSamples > 0
      ? ((currentIndex + 1) / totalSamples) * 100
      : 0;

  return {
    currentTrial,
    currentSample,
    currentIndex,
    totalSamples,
    progress,
    loading: !session || !task || !trials.length || !Object.keys(samples).length,
    submitting,
    showFeedback,
    feedbackRows,
    currentResponse,
    submitEvaluation,
    next,
    isCompleted,
    step,
    isContinuousPractice,
  };
}
